// /:partnerId/signup — registro (Auth0 dbconnections/signup).
//
// La lógica del form vive en una máquina de estados (XState): ver
// @/lib/signup-machine. Esta página es SÓLO la proyección visual — deriva todo
// del snapshot y despacha eventos. No hay orquestación async acá: el gate
// (validación sync + existencia/deliverability del email + fail-closed) es
// responsabilidad de la máquina, por diseño (era donde se rompía antes).
//
// Capa visual: cada campo lleva un contorno de estado ENVOLVENTE (FieldStatus,
// idle/checking/valid/invalid) derivado del context; las objeciones graves del
// email escalan a una hoja (EmailObjectionSheet) SÓLO al intentar continuar.

import { useEffect, useRef, useState, type RefObject } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMachine } from '@xstate/react';
import AuthShell from '@/components/AuthShell';
import FormField, { type FieldStatus } from '@/components/FormField';
import SubmitButton from '@/components/SubmitButton';
import PasswordChecklist, { EmailMatchHint } from '@/components/PasswordChecklist';
import EmailStatus from '@/components/EmailStatus';
import EmailObjectionSheet, { type ObjectionVariant } from '@/components/EmailObjectionSheet';
import { webmailLink } from '@/lib/webmail';
import { btn } from '@/lib/button-styles';
import { BackStepLink, FlowExitLink } from '@/components/FlowNav';
import SearchableFavoriteSelect from '@/components/SearchableFavoriteSelect';
import PlanPicker from '@/components/PlanPicker';
import { useAuthParams } from '@/lib/auth-params';
import { stashPayReturn } from '@/lib/pay-return';
import { fetchSignupConfig, fetchTosConfig } from '@/lib/api';
import { useTheme } from '@/theme/ThemeProvider';
import { PASSWORD_MIN_LENGTH, PASSWORD_RULES_ATTR, isPasswordValid } from '@/lib/password-policy';
import { scrollToField, scrollToGroup } from '@/lib/dom';
import { signupMachine, emailVerdictOf } from '@/lib/signup-machine';
import type { FieldKey } from '@/lib/signup-schema';

export default function Signup(): JSX.Element {
  const { partnerId = '' } = useParams<{ partnerId: string }>();
  const authz = useAuthParams();
  const theme = useTheme();

  // La máquina es la autoridad única del ciclo del form. La página proyecta su
  // snapshot y despacha eventos; no mantiene estado de validación propio.
  const [state, send] = useMachine(signupMachine, { input: { partnerId, authz } });
  const ctx = state.context;

  // Hoja de objeciones graves (view-only). Se abre desde el efecto de submit.
  const [sheet, setSheet] = useState<ObjectionVariant | null>(null);

  // URL de T&C vigente (display-only; no toca la máquina). Fallback al branding.
  const [tosConfigUrl, setTosConfigUrl] = useState<string | null>(null);
  const brandingTosUrl =
    theme.state.status === 'ready' ? theme.state.branding.tos_url : undefined;
  const tosUrl = tosConfigUrl ?? brandingTosUrl;
  const privacyUrl =
    theme.state.status === 'ready' ? theme.state.branding.privacy_url : undefined;
  const branding = theme.state.status === 'ready' ? theme.state.branding : null;
  const contestName = branding?.contest_name?.trim() ?? '';
  const contestUrl = branding?.contest_terms_url?.trim() ?? '';
  const contestActive = Boolean(branding?.contest_active && contestName && contestUrl);

  // Refs para el scroll al primer campo con error tras un submit fallido.
  const fieldRefs: Record<FieldKey, RefObject<HTMLInputElement>> = {
    first_name: useRef<HTMLInputElement>(null),
    last_name: useRef<HTMLInputElement>(null),
    email: useRef<HTMLInputElement>(null),
    email_confirm: useRef<HTMLInputElement>(null),
    password: useRef<HTMLInputElement>(null),
    terms: useRef<HTMLInputElement>(null),
  };
  // Grupo de los dos emails: el scroll inteligente encuadra el par, no un campo.
  const emailGroupRef = useRef<HTMLDivElement>(null);
  // Dedupe de la hoja de objeciones por `email|tipo`: NO por email solo, porque un
  // mismo email puede encadenar objeciones (typo → "dejar así" → dominio no
  // entregable / ya existe). Cada tipo NUEVO se muestra una vez; el mismo tipo no
  // reaparece en cada render/blur.
  const sheetShownForRef = useRef<string | null>(null);

  // Config del partner (planes + favoritos requeridos) y URL de T&C. Fail-open:
  // si falla, la máquina sigue con sus defaults (plansEnabled=false, sin favoritos).
  // Deps PRIMITIVAS a propósito: este efecto hace red, y con un objeto inestable
  // en las deps se re-dispara en cada render (ver useAuthParams — causa raíz de
  // los 3,1M req/día a /tos en prod). Un solo fetch por montaje, siempre.
  const clientId = authz.ok ? authz.params.client_id : undefined;
  useEffect(() => {
    if (!partnerId) return;
    fetchSignupConfig(partnerId, clientId)
      .then((cfg) => {
        if (cfg) {
          send({
            type: 'CONFIG_LOADED',
            plansEnabled: cfg.plans_enabled === true,
            requiredFavorites: cfg.required_favorites,
          });
        }
      })
      .catch(() => {});
    fetchTosConfig(partnerId)
      .then((cfg) => {
        if (cfg?.tos_url) setTosConfigUrl(cfg.tos_url);
      })
      .catch(() => {});
  }, [partnerId, clientId, send]);

  // Navegación de salida: la máquina decide el desenlace (redirect / pago) y deja
  // el destino en el context; acá lo ejecutamos (window vive en React-land).
  useEffect(() => {
    if (!state.matches('redirecting')) return;
    if (ctx.redirectTo) {
      window.location.href = ctx.redirectTo;
      return;
    }
    if (ctx.payToken) {
      const payUrl = new URL(`${window.location.origin}/${partnerId}/pay`);
      payUrl.searchParams.set('pt', ctx.payToken);
      // El contexto de retorno a la app viaja por sessionStorage, NO por la URL:
      // el `pt` es compartible y una URL forjable convertiría "puedo pagar" en
      // "entro como el titular". Ver lib/pay-return.ts.
      if (authz.ok) {
        stashPayReturn(ctx.payToken, {
          client_id: authz.params.client_id,
          redirect_uri: authz.params.redirect_uri,
          state: authz.params.state,
          code_challenge: authz.params.code_challenge,
          code_challenge_method: 'S256',
          scope: authz.params.scope,
          ...(authz.params.nonce && { nonce: authz.params.nonce }),
        });
      }
      window.location.href = payUrl.toString();
    }
  }, [state, ctx.redirectTo, ctx.payToken, partnerId, authz]);

  // Derivados del snapshot (misma fuente que el gate: sin señales paralelas).
  const emailVerdict = emailVerdictOf(ctx);
  const submitting =
    state.matches({ details: 'validating' }) ||
    state.matches({ details: 'checkingForSubmit' }) ||
    state.matches('creating');
  // La máquina está consultando existencia/dominio del email → spinner "Verificando…".
  const emailChecking =
    state.matches({ details: 'checkingEmail' }) || state.matches({ details: 'checkingForSubmit' });

  // Scroll + foco al primer campo que frenó el submit; si es una objeción grave
  // del email (existe / typo / dominio no recibe), ESCALA a la hoja en vez de
  // sólo marcar. `scrollNonce` cambia en cada gate fallido → re-dispara.
  useEffect(() => {
    const f = ctx.firstErrorField;
    if (!f) return;
    const k = emailVerdict.kind;
    if (f === 'email' && (k === 'exists' || k === 'undeliverable' || k === 'typo')) {
      setSheet(k);
      return;
    }
    if (f === 'email' || f === 'email_confirm') {
      scrollToGroup(emailGroupRef.current, fieldRefs[f].current);
    } else {
      scrollToField(fieldRefs[f].current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.scrollNonce]);

  // La hoja de objeciones también aparece al CONFIRMARSE la objeción en el blur
  // (no sólo al intentar continuar): apenas el check confirma existe/no entregable,
  // o se detecta un typo. Una vez por email (dedupe) para no ser intrusivo; al
  // editar el email `emailTouched` se resetea y la próxima dirección la re-habilita.
  useEffect(() => {
    if (!ctx.emailTouched) return;
    const k = emailVerdict.kind;
    if (k !== 'exists' && k !== 'undeliverable' && k !== 'typo') return;
    const email = ctx.values.email.trim().toLowerCase();
    if (!email) return;
    const key = `${email}|${k}`;
    if (sheetShownForRef.current === key) return;
    sheetShownForRef.current = key;
    setSheet(k);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailVerdict.kind, ctx.emailTouched, ctx.values.email]);

  // Estado visual por campo, derivado del context (misma fuente que el gate).
  // idle = sin anillo; el verde sólo cuando el dato es VERDAD (email tras el check
  // async; password con las 4 reglas). El rojo de los campos no-email llega recién
  // al intentar continuar (fieldErrors se puebla en el gate).
  const statusOf = (f: FieldKey): FieldStatus => {
    if (ctx.fieldErrors[f]) return 'invalid';
    switch (f) {
      case 'first_name':
      case 'last_name':
        return ctx.values[f].trim().length > 0 ? 'valid' : 'idle';
      case 'email': {
        if (!ctx.emailTouched) return 'idle';
        const k = emailVerdict.kind;
        if (k === 'checking') return 'checking';
        if (k === 'ok') return 'valid';
        if (k === 'exists' || k === 'undeliverable' || k === 'invalid_format' || k === 'bad_tld')
          return 'invalid';
        return 'idle';
      }
      case 'email_confirm': {
        if (!ctx.emailTouched) return 'idle';
        const hasConfirm = ctx.values.email_confirm.trim().length > 0;
        if (emailVerdict.kind === 'mismatch' && hasConfirm) return 'invalid';
        if (emailVerdict.kind === 'ok' && hasConfirm) return 'valid';
        return 'idle';
      }
      case 'password':
        return isPasswordValid(ctx.values.password) ? 'valid' : 'idle';
      case 'terms':
        return 'idle';
      default:
        return 'idle';
    }
  };
  const isInvalid = (f: FieldKey): boolean => Boolean(ctx.fieldErrors[f]);

  // Estado del GRUPO de emails: un solo marco envuelve ambos campos + las
  // confirmaciones. success = coinciden y disponible; error = cualquier objeción
  // (existe / no entregable / formato / no coinciden); checking mientras verifica.
  const emailGroupStatus = ((): FieldStatus => {
    // Mientras corre el chequeo async, el marco pulsa (feedback en la transición
    // de la cadena: typo → "dejar así" → verificando → resultado).
    if (emailChecking) return 'checking';
    if (!ctx.emailTouched) return 'idle';
    // "No pudimos verificar" es un WARNING (ámbar), no un error rojo del usuario.
    if (ctx.emailWarning) return 'warning';
    if (ctx.fieldErrors.email || ctx.fieldErrors.email_confirm) return 'invalid';
    switch (emailVerdict.kind) {
      case 'checking':
        return 'checking';
      case 'ok':
        return 'valid';
      case 'typo':
      case 'exists':
      case 'undeliverable':
      case 'invalid_format':
      case 'bad_tld':
        // Todas bloquean el submit → marco rojo (el typo también: decideSubmit
        // lo trata como 'block' hasta que se acepte/descarte).
        return 'invalid';
      case 'mismatch':
        return emailVerdict.confirmEmpty ? 'idle' : 'invalid';
      default:
        return 'idle';
    }
  })();

  // --- Desenlace: redirect en curso ---
  if (state.matches('redirecting')) {
    return (
      <AuthShell title="Un momento…" subtitle="Te estamos redirigiendo.">
        <p className="text-sm text-text-muted" aria-live="polite">
          Redirigiendo…
        </p>
      </AuthShell>
    );
  }

  // --- Desenlace: alta creada, verificación por email pendiente ---
  if (state.matches('submitted') && ctx.submittedEmail) {
    const mail = webmailLink(ctx.submittedEmail);
    // El re-chequeo reusa /auth/login: necesita los params del flow OAuth y la
    // contraseña que el usuario acaba de tipear (sigue en memoria, no se
    // persiste). Sin eso no hay botón.
    const canReCheck = ctx.authz.ok && ctx.values.password.length > 0;
    const checking = state.matches({ submitted: 'checking' });
    const notYet = state.matches({ submitted: 'notYet' });
    const checkFailed = state.matches({ submitted: 'checkFailed' });
    const verifiedNeedsConsent = state.matches({ submitted: 'verified' });
    return (
      <AuthShell
        title="Revisá tu correo"
        subtitle="Te mandamos un mail para activar la cuenta."
        footerLinks={
          <button type="button" onClick={() => send({ type: 'CREATE_ANOTHER' })} className="hover:underline">
            Crear otra cuenta
          </button>
        }
      >
        <div className="space-y-4">
          <div
            className="mx-auto grid place-items-center h-14 w-14 rounded-2xl bg-success/15 text-success"
            aria-hidden="true"
          >
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 12.5 9.5 18 20 6.5" />
            </svg>
          </div>
          <p className="text-sm text-text">
            Abrí el mail que te mandamos a <strong>{ctx.submittedEmail}</strong> y activá tu cuenta.
          </p>
          {ctx.pendingPaidPlan ? (
            <div className="rounded-lg border border-border bg-surface p-3 text-sm text-text">
              Después de verificar, completás el pago de{' '}
              <strong>{ctx.pendingPaidPlan.display_name}</strong>.
            </div>
          ) : null}
          {mail ? (
            <a
              href={mail.url}
              target="_blank"
              rel="noopener noreferrer"
              className={btn('accent', 'md', 'w-full')}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="5" width="14" height="10" rx="2" />
                <path d="M4 6l6 5 6-5" />
              </svg>
              Abrir {mail.label}
            </a>
          ) : null}
          <p className="text-xs text-text-muted">¿No te llegó? Mirá en Spam. Vence en 24 h.</p>

          {/* Re-chequeo: el usuario verifica en otra pestaña o en el celular y
              vuelve acá. Es un reintento real del login, así que un éxito lo
              devuelve a la app en el acto en vez de dejarlo buscando dónde
              entrar. Solo aparece si el alta vino de un flow OAuth (hay a dónde
              volver) y todavía tenemos la contraseña en memoria. */
          canReCheck ? (
            <div className="rounded-lg border border-border bg-surface p-3">
              <button
                type="button"
                disabled={checking}
                onClick={() => send({ type: 'VERIFY_CHECK' })}
                className={btn('secondary', 'md', 'w-full')}
              >
                {checking ? 'Comprobando…' : 'Ya validé mi correo'}
              </button>

              {verifiedNeedsConsent ? (
                <p role="status" className="mt-2 text-sm text-success">
                  ¡Listo! Tu correo quedó verificado. Iniciá sesión para terminar de
                  aceptar los términos y volver a la app.
                </p>
              ) : null}

              {notYet ? (
                <p role="status" className="mt-2 text-sm text-text">
                  Todavía no nos figura verificado. Abrí el link del mail y volvé a
                  tocar el botón.
                </p>
              ) : null}

              {checkFailed ? (
                <p role="alert" className="mt-2 text-sm text-error">
                  {ctx.verifyError}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      </AuthShell>
    );
  }

  const creating = state.matches('creating');
  const onPlanStep = state.matches('plan') || (creating && ctx.plansEnabled);

  // --- Paso 2: elección de plan ---
  if (onPlanStep) {
    const planIsPaid = Boolean(ctx.selectedPlan && ctx.selectedPlan.price_amount > 0);
    return (
      <AuthShell
        title="Elegí tu plan"
        subtitle="Empezá gratis o desbloqueá todo con Premium."
        footerLinks={<BackStepLink onClick={() => send({ type: 'BACK' })} />}
        flowExit={
          authz.ok ? (
            <FlowExitLink
              exit={{
                kind: 'oauth',
                redirectUri: authz.params.redirect_uri,
                state: authz.params.state,
              }}
            />
          ) : null
        }
      >
        <div className="space-y-4">
          <PlanPicker
            partnerId={partnerId}
            value={ctx.selectedPlan?.plan_id ?? null}
            onChange={(_planId, plan) => send({ type: 'SELECT_PLAN', plan })}
          />
          {ctx.formError ? (
            <div
              role="alert"
              aria-live="polite"
              className="rounded-lg border border-error/30 bg-error/10 p-3 text-sm text-error"
            >
              {ctx.formError}
            </div>
          ) : null}
          <SubmitButton
            type="button"
            onClick={() => send({ type: 'CONFIRM_PLAN' })}
            disabled={!ctx.selectedPlan}
            loading={creating}
            loadingLabel="Creando cuenta…"
          >
            {planIsPaid ? 'Crear cuenta y pagar' : 'Crear cuenta'}
          </SubmitButton>
        </div>
      </AuthShell>
    );
  }

  // --- Paso 1: datos de la cuenta ---
  return (
    <AuthShell
      title="Creá tu cuenta"
      subtitle="y sumate a la comunidad del fútbol argentino."
      footerLinks={
        authz.ok ? (
          <Link to={`/${partnerId}/login${window.location.search}`} className="hover:underline">
            Ya tengo cuenta — ingresar
          </Link>
        ) : null
      }
      flowExit={
        authz.ok ? (
          <FlowExitLink
            exit={{
              kind: 'oauth',
              redirectUri: authz.params.redirect_uri,
              state: authz.params.state,
            }}
          />
        ) : null
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          send({ type: 'SUBMIT' });
        }}
        aria-busy={submitting ? 'true' : undefined}
        className="space-y-4"
        noValidate
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FormField
            ref={fieldRefs.first_name}
            name="first_name"
            type="text"
            label="Nombre"
            autoComplete="given-name"
            autoFocus
            required
            maxLength={100}
            disabled={submitting}
            status={statusOf('first_name')}
            value={ctx.values.first_name}
            onChange={(e) => send({ type: 'SET_FIELD', field: 'first_name', value: e.target.value })}
          />
          <FormField
            ref={fieldRefs.last_name}
            name="last_name"
            type="text"
            label="Apellido"
            autoComplete="family-name"
            required
            maxLength={100}
            disabled={submitting}
            status={statusOf('last_name')}
            value={ctx.values.last_name}
            onChange={(e) => send({ type: 'SET_FIELD', field: 'last_name', value: e.target.value })}
          />
        </div>
        {/* Grupo de emails: UN SOLO marco envuelve ambos campos + las
            confirmaciones/objeciones, con el anillo de estado por el CONJUNTO
            (los inputs van `frameless`). El PRIMER email deja pegar y
            autocompletar; el SEGUNDO se tipea a mano (bloquea paste) para que un
            typo pegado dos veces no pase. */}
        <div ref={emailGroupRef} className="field-group" data-status={emailGroupStatus}>
          <FormField
            ref={fieldRefs.email}
            frameless
            name="email"
            type="email"
            label="Email"
            autoComplete="email"
            required
            disabled={submitting}
            value={ctx.values.email}
            onChange={(e) => send({ type: 'SET_FIELD', field: 'email', value: e.target.value })}
            onBlur={() => send({ type: 'BLUR_EMAIL' })}
          />
          <FormField
            ref={fieldRefs.email_confirm}
            frameless
            name="email_confirm"
            type="email"
            label="Repetí tu email"
            autoComplete="off"
            onPaste={(e) => e.preventDefault()}
            onDrop={(e) => e.preventDefault()}
            required
            disabled={submitting}
            value={ctx.values.email_confirm}
            onChange={(e) => send({ type: 'SET_FIELD', field: 'email_confirm', value: e.target.value })}
            onBlur={() => send({ type: 'BLUR_EMAIL' })}
            footer={
              <span className="text-xs text-text-muted">Escribilo a mano.</span>
            }
          />
          <EmailMatchHint email={ctx.values.email} confirm={ctx.values.email_confirm} />
          <EmailStatus
            verdict={emailVerdict}
            touched={ctx.emailTouched}
            checking={emailChecking}
            warning={ctx.emailWarning}
          />
        </div>
        <FormField
          ref={fieldRefs.password}
          name="password"
          type="password"
          label="Contraseña"
          autoComplete="new-password"
          minLength={PASSWORD_MIN_LENGTH}
          passwordRules={PASSWORD_RULES_ATTR}
          revealToggle
          required
          disabled={submitting}
          status={statusOf('password')}
          value={ctx.values.password}
          onChange={(e) => send({ type: 'SET_FIELD', field: 'password', value: e.target.value })}
          footer={
            <div className="pt-1">
              <PasswordChecklist password={ctx.values.password} />
            </div>
          }
        />
        {ctx.requiredFavorites.length > 0 ? (
          <div className="space-y-3 pt-2">
            {ctx.requiredFavorites.map((rf) => (
              <SearchableFavoriteSelect
                key={rf.favorite_type_id}
                partnerId={partnerId}
                favoriteTypeId={rf.favorite_type_id}
                label={rf.label}
                titleField={rf.title_field}
                imageField={rf.image_field}
                initialLimit={rf.initial_limit}
                status={
                  ctx.missingFavorites.includes(rf.favorite_type_id)
                    ? 'invalid'
                    : ctx.values.favorites[rf.favorite_type_id]
                      ? 'valid'
                      : 'idle'
                }
                value={ctx.values.favorites[rf.favorite_type_id] ?? null}
                onChange={(v) =>
                  send({ type: 'SET_FAVORITE', favoriteTypeId: rf.favorite_type_id, value: v })
                }
              />
            ))}
          </div>
        ) : null}
        <label
          className={`flex items-start gap-2 text-sm ${
            isInvalid('terms') ? 'text-error' : 'text-text'
          }`}
        >
          <input
            ref={fieldRefs.terms}
            type="checkbox"
            checked={ctx.values.terms_accepted}
            onChange={(e) => send({ type: 'TOGGLE', field: 'terms_accepted', value: e.target.checked })}
            required
            disabled={submitting}
            aria-invalid={isInvalid('terms') ? 'true' : undefined}
            className={`mt-1 rounded ${
              isInvalid('terms') ? 'ring-2 ring-error ring-offset-1 ring-offset-background' : ''
            }`}
          />
          <span>
            Acepto los{' '}
            <a
              href={tosUrl ?? '#'}
              target="_blank"
              rel="noreferrer"
              className="underline"
              {...(!tosUrl && { onClick: (e) => e.preventDefault() })}
            >
              Términos y Condiciones
            </a>
            {contestActive ? ',' : ' y'} la{' '}
            <a
              href={privacyUrl ?? '#'}
              target="_blank"
              rel="noreferrer"
              className="underline"
              {...(!privacyUrl && { onClick: (e) => e.preventDefault() })}
            >
              Política de Privacidad
            </a>
            {contestActive ? (
              <>
                {' '}y{' '}
                <a href={contestUrl} target="_blank" rel="noreferrer" className="underline">
                  {contestName}
                </a>
              </>
            ) : null}
            .
          </span>
        </label>
        <label className="flex items-start gap-2 text-sm text-text-muted">
          <input
            type="checkbox"
            checked={ctx.values.marketing_opt_in}
            onChange={(e) => send({ type: 'TOGGLE', field: 'marketing_opt_in', value: e.target.checked })}
            disabled={submitting}
            className="mt-1"
          />
          <span>Quiero recibir novedades por email (opcional).</span>
        </label>
        {ctx.formError ? (
          <div
            role="alert"
            aria-live="polite"
            className="rounded-lg border border-error/30 bg-error/10 p-3 text-sm text-error"
          >
            <p>{ctx.formError}</p>
            {ctx.showRecoverLink ? (
              <p className="mt-1">
                <Link to={`/${partnerId}/forgot-password${window.location.search}`} className="underline">
                  Recuperar contraseña
                </Link>
              </p>
            ) : null}
          </div>
        ) : null}
        <SubmitButton loading={submitting} loadingLabel="Verificando…">
          Continuar
        </SubmitButton>
      </form>

      <EmailObjectionSheet
        variant={sheet}
        partnerId={partnerId}
        suggestion={emailVerdict.kind === 'typo' ? emailVerdict.suggestion : null}
        onModify={() => {
          setSheet(null);
          fieldRefs.email.current?.focus();
        }}
        onAcceptTypo={() => send({ type: 'ACCEPT_TYPO' })}
        onKeepTypo={() => send({ type: 'KEEP_TYPO' })}
        onClose={() => setSheet(null)}
      />
    </AuthShell>
  );
}
